import { fixUrl } from '@flowus/common/embed-website';
import { BlockType } from '@next-space/fe-api-idl';
import type { IEditorModel } from '@next-space/fe-inlined';
import { findItemPathInRange, newContent, newElement, newText } from '@next-space/fe-inlined/utils';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { ListItemType, ListView } from 'src/common/components/list-view';
import type { ModalSchema } from 'src/common/components/next-modal';
import { request } from 'src/common/request';
import { getEmbedThirdParty } from 'src/editor/component/menu-list/embeds-data';
import { useCopyView, useOpenCopyView } from 'src/editor/component/menu-list/use-open-copy-view';
import {
  INLINE_LINK_PAGE_TAG,
  INLINE_MENTION_BLOCK_TAG,
  LINK_TAG,
} from 'src/editor/editor/inline/const';
import { segmentsToText } from 'src/editor/utils/editor';
import { ActivityIds } from 'src/hooks/activities/activity-ids';
import { useUpdateTask } from 'src/hooks/activities/use-update-task';
import { ActivitiesListType } from 'src/hooks/activities/use-activity';
import { getOwnerPage } from 'src/hooks/block/use-get-owner-page';
import type { FocusEditableFun } from 'src/hooks/editor/use-focus-by-id';
import { useTransaction } from 'src/hooks/use-transaction';
import { HISTORY_EFFECTS, HISTORY_UNDO } from 'src/redux/actions';
import { addBlock } from 'src/redux/managers/block/add';
import { convertBlock, covertToSyncBlock } from 'src/redux/managers/block/convert';
import { updateBlock } from 'src/redux/managers/block/update';
import { blocksActions } from 'src/redux/reducers/blocks';
import { collectionViewsActions } from 'src/redux/reducers/collection-views';
import { commentsActions } from 'src/redux/reducers/comments';
import { discussionsActions } from 'src/redux/reducers/discussions';
import { uiActions } from 'src/redux/reducers/ui';
import { cache, dispatch, getState } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { ViewPath } from 'src/utils';
import { isInSyncBlock } from 'src/utils/block-utils';
import { blurDocument } from 'src/utils/dom';
import { getBlockNodeFromEditorKey, getRowIdFromEditorKey } from 'src/utils/editor-utils';
import { parsePageId } from 'src/utils/getPageId';
import { getLocationOrigin } from 'src/utils/location-utils';
import { validate } from 'uuid';
import { checkViewPath } from 'src/common/utils/url-utils';
import { LOCAL_LNG } from '@flowus/common/const';

interface Props {
  id: string;
  url: string;
  model: IEditorModel;
  posBlock?: NextBlock;
  cursorPos: number;
  onCloseModal: () => void;
  focusEditableAt: FocusEditableFun;
  isComment: boolean;
  popcorn: ModalSchema.PopcornType;
}

export const PasteLinkPanel: FC<Props> = ({
  id,
  url,
  model,
  posBlock,
  isComment,
  cursorPos,
  onCloseModal,
  focusEditableAt,
  popcorn,
}) => {
  const transaction = useTransaction();
  const updateTask = useUpdateTask();
  const openCopyView = useOpenCopyView();
  const copyViewFun = useCopyView();
  const [linkInfo, setLinkInfo] = useState<{
    pageId: string;
    blockId?: string;
    isSync: boolean;
    viewId?: string;
  }>();
  const { selection } = model;
  const urlObj = new URL(fixUrl(url));

  useEffect(() => {
    const { selection } = model;
    if (!selection) return;
    if (
      !url.startsWith(getLocationOrigin()) ||
      checkViewPath(ViewPath.share, 'includes', urlObj.pathname) ||
      checkViewPath(ViewPath.preview, 'includes', urlObj.pathname)
    ) {
      return;
    }
    const pageId = parsePageId(urlObj.pathname);
    let blockId: string = urlObj.hash.slice(1).substring(0, 36);
    if (blockId) {
      if (!validate(blockId)) {
        blockId = '';
      }
    }
    const checkBlockExist = () => {
      const pageBlock = cache.blocks[pageId];
      const block = cache.blocks[blockId];
      if (pageBlock) {
        if (
          pageBlock.type === BlockType.COLLECTION_VIEW ||
          pageBlock.type === BlockType.COLLECTION_VIEW_PAGE
        ) {
          setLinkInfo({
            pageId,
            isSync: false,
            viewId: blockId,
          });
          return true;
        }

        const isSync = isInSyncBlock(blockId) ?? false;
        // 如果没有blockId或者有block信息才设置，否则blockId可能是假的，需要继续走下面的逻辑判断一下
        if (!blockId || (blockId && block)) {
          setLinkInfo({
            pageId,
            blockId,
            isSync,
          });
          return true;
        }
      }
    };
    if (checkBlockExist()) return;
    // 如果没有块信息，可能是其他窗口复制来的，需要拉一下数据确定能拉到
    void fetchPage(pageId, blockId).then((res) => {
      if (res) {
        checkBlockExist();
      }
    });
  }, [model, url, urlObj.hash, urlObj.pathname]);

  if (!selection) return null;

  const web = Object.values(getEmbedThirdParty()).find((o) => {
    // 这里之前这么写的正则，safari 报错崩了 /(?<=https:\/\/)(www\.)?(.+)(?=\/)/
    const match = o.data.placeholder.match(/https:\/\/(.+)(?=\/)/);
    if (match) {
      return url.includes((match[1] as string).replace(/www\./, ''));
    }
    return false;
  });

  const createBlock = (type: BlockType) => {
    const blockNode = getBlockNodeFromEditorKey(model.editorKey);
    if (!blockNode) return;
    const { syncId } = blockNode.dataset;
    let selectedId = id;
    if (posBlock) {
      const { parentId, data } = posBlock;
      updateBlock(id, { data: { segments: data.segments ?? [] } });
      selectedId = addBlock({ type, data: { link: url } }, { parentId, after: id });
    } else {
      updateBlock(id, { type, data: { link: url }, local: true });
    }

    dispatch(
      HISTORY_EFFECTS({
        init() {
          blurDocument();
          dispatch(uiActions.updateSelectBlocks([{ blockId: selectedId, syncId }]));
        },
        redo() {
          blurDocument();
          dispatch(uiActions.updateSelectBlocks([{ blockId: selectedId, syncId }]));
        },
        undo() {
          focusEditableAt(id, cursorPos);
        },
      })
    );
  };

  const ownerPage = getOwnerPage(linkInfo?.blockId ?? '');

  const _id = getRowIdFromEditorKey(id);
  const isTable = _id !== id;
  const isWebUrl = /^[http]/.test(urlObj.protocol);

  const blockType = !linkInfo?.blockId && getState().blocks[linkInfo?.pageId ?? '']?.type;
  let isCollection = false;
  if (
    blockType === BlockType.REFERENCE_COLLECTION ||
    blockType === BlockType.REFERENCE_COLLECTION_PAGE ||
    blockType === BlockType.COLLECTION_VIEW ||
    blockType === BlockType.COLLECTION_VIEW_PAGE
  ) {
    isCollection = true;
  }

  const items = linkInfo
    ? [
        {
          type: ListItemType.BLOCK_TRANSFORM,
          data: {
            title: '保持链接形式',
          },
        },
        {
          type: ListItemType.LINE,
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.BLOCK_TRANSFORM,
          isHidden:
            isTable ||
            !(linkInfo && linkInfo.blockId && !linkInfo.isSync) ||
            cache.blocks[ownerPage ?? '']?.type === BlockType.FOLDER, // 文件夹内的文件粘贴后无法转成同步块，因此要隐藏
          data: {
            title: `粘贴为同步块`,
            icon: 'MIcInsertSyncedBlock',
            onClick() {
              if (!linkInfo) return;
              const text = segmentsToText(posBlock?.data.segments);
              if (text && posBlock) {
                dispatch(HISTORY_UNDO()); // 清掉链接
                transaction(() => {
                  const newBlockId = addBlock(
                    {},
                    { parentId: posBlock.parentId, after: posBlock.uuid }
                  );
                  const newSyncBlockId = covertToSyncBlock(
                    [linkInfo.blockId ?? ''],
                    cache.blocks[linkInfo.blockId ?? '']?.parentId ?? ''
                  );
                  if (!newSyncBlockId) return;
                  convertBlock([newBlockId], {
                    type: BlockType.SYNC_REFERENCE,
                    data: {
                      segments: [],
                      ref: {
                        uuid: newSyncBlockId,
                      },
                    },
                  });
                });
              } else {
                transaction(() => {
                  const newSyncBlockId = covertToSyncBlock(
                    [linkInfo.blockId ?? ''],
                    cache.blocks[linkInfo.blockId ?? '']?.parentId ?? ''
                  );
                  if (!newSyncBlockId) return;
                  convertBlock([id], {
                    type: BlockType.SYNC_REFERENCE,
                    data: {
                      segments: [],
                      ref: {
                        uuid: newSyncBlockId,
                      },
                    },
                  });
                });
              }
            },
          },
        },
        {
          type: ListItemType.BLOCK_TRANSFORM,
          isHidden: !(linkInfo && linkInfo.blockId),
          data: {
            title: `粘贴为行内块引用`,
            icon: 'MIcInsertPageInline',
            onClick() {
              model.performChange((ctx) => {
                ctx
                  .select(selection.offset - newText(url).length, selection.endOffset)
                  .replace(
                    newContent([
                      newElement(INLINE_MENTION_BLOCK_TAG, {
                        pageId: linkInfo?.pageId,
                        blockId: linkInfo?.blockId,
                      }),
                    ])
                  )
                  .collapse('end');
              });
            },
          },
        },
        {
          type: ListItemType.BLOCK_TRANSFORM,
          isHidden: isTable || isComment || !(linkInfo && linkInfo.pageId && !linkInfo.blockId),
          data: {
            title: `粘贴为引用页面`,
            icon: 'MIcInsertLinktopage',
            onClick() {
              const text = segmentsToText(posBlock?.data.segments);
              if (text && posBlock) {
                dispatch(HISTORY_UNDO()); // 清掉链接
                transaction(() => {
                  addBlock(
                    {
                      type: BlockType.REFERENCE,
                      data: {
                        ref: {
                          uuid: linkInfo?.pageId,
                        },
                        segments: [],
                        display: 'linkPage',
                      },
                    },
                    {
                      parentId: posBlock.parentId,
                      after: posBlock.uuid,
                    }
                  );
                });
              } else {
                transaction(() => {
                  convertBlock([id], {
                    type: BlockType.REFERENCE,
                    data: {
                      ref: {
                        uuid: linkInfo?.pageId,
                      },
                      segments: [],
                      display: 'linkPage',
                    },
                  });
                });
              }
            },
          },
        },
        {
          isHidden: !(linkInfo && linkInfo.pageId && !linkInfo.blockId),
          type: ListItemType.BLOCK_TRANSFORM,
          data: {
            title: `粘贴为行内引用页面`,
            icon: 'MIcInsertPageInline',
            onClick() {
              model.performChange((ctx) => {
                ctx
                  .select(selection.endOffset - newText(url).length, selection.endOffset)
                  .replace(
                    newContent([
                      newElement(INLINE_LINK_PAGE_TAG, {
                        pageId: linkInfo?.pageId,
                      }),
                    ])
                  )
                  .collapse('end');
              });
            },
          },
        },
        {
          isHidden: !isCollection,
          type: ListItemType.BLOCK_TRANSFORM,
          data: {
            title: `粘贴为引用多维表`,
            icon: 'MIcInsertPageInline',
            onClick() {
              if (!posBlock?.uuid) return;
              // 带了viewId的直接copy为viewId的那个view
              if (linkInfo.viewId) {
                void copyViewFun({
                  collectionId: linkInfo.pageId,
                  blockId: posBlock.uuid,
                  isCopyLink: true,
                  viewId: linkInfo.viewId,
                });
                return;
              }

              void openCopyView({
                popcorn,
                collectionId: linkInfo.pageId,
                blockId: posBlock.uuid,
                isCopyLink: true,
              });
            },
          },
        },
      ]
    : [
        {
          type: ListItemType.BLOCK_TRANSFORM,
          data: {
            title: '保持链接形式',
          },
        },
        {
          type: ListItemType.LINE,
          data: {},
          disableClick: true,
        },
        {
          type: ListItemType.BLOCK_TRANSFORM,
          data: {
            title: '转换为 纯文本',
            icon: 'MIcInsertBlockText',
            onClick() {
              model.performChange((ctx) => {
                const linkPath = findItemPathInRange(
                  model.content,
                  selection.offset,
                  selection.endOffset,
                  (item) => {
                    return (
                      item.type === 'element' && item.tag === LINK_TAG && selection.isCollapsed
                    );
                  }
                );
                ctx.select(selection.offset, selection.endOffset);
                if (linkPath.length > 0) {
                  ctx.unwrap(LINK_TAG, linkPath);
                }
              });
            },
          },
        },
        {
          type: ListItemType.BLOCK_TRANSFORM,
          isHidden: isTable || isComment || !isWebUrl,
          data: {
            title: '转换为 网页书签',
            icon: 'MIcInsertBlockBookmark',
            onClick() {
              const text = segmentsToText(posBlock?.data.segments);
              if (text) {
                transaction(() => createBlock(BlockType.BOOKMARK));
              } else {
                transaction(() =>
                  convertBlock([id], {
                    type: BlockType.BOOKMARK,
                    data: {
                      link: url,
                      caption: [],
                    },
                  })
                );
              }

              // 积分任务
              void updateTask(ActivityIds.CREATE_WEB_BOOKMARK, ActivitiesListType.advancedList);
            },
          },
        },
        {
          type: ListItemType.BLOCK_TRANSFORM,
          isHidden: isTable || isComment || !isWebUrl,
          data: {
            title: web ? ['转换为', web.data.title].join(' ') : LOCAL_LNG.embedWebPage,
            image: web ? web.data.image : '',
            icon: web ? '' : 'MIcInsertBlockWeb',
            onClick() {
              const text = segmentsToText(posBlock?.data.segments);
              if (text) {
                transaction(() => createBlock(BlockType.EMBED));
              } else {
                transaction(() =>
                  convertBlock([id], {
                    type: BlockType.EMBED,
                    data: {
                      link: url,
                    },
                  })
                );
              }
            },
          },
        },
      ];
  return (
    <ListView
      className="py-2 w-56 next-modal"
      items={items}
      onItemClick={() => onCloseModal()}
      onGlobalCaptureKeydown={onCloseModal}
    />
  );
};

const fetchPage = async (pageId: string, blockId: string) => {
  const res = await request.editor.getDoc.raw(pageId);
  if (res.code === 200) {
    const { blocks, collectionViews, discussions, comments } = res.data;
    // 如果没有子块说明blockId是假的，不需显示对应的选项
    if (blockId && !blocks[blockId]) return false;
    if (collectionViews) {
      dispatch(collectionViewsActions.update(collectionViews));
    }
    if (discussions) {
      dispatch(discussionsActions.update(discussions));
    }
    if (comments) {
      dispatch(commentsActions.update(comments));
    }
    dispatch(blocksActions.update({ blocks: blocks as Record<string, NextBlock> }));
    return true;
  }
  return false;
};
